import { FeaturePageLayout } from '../../components/features/FeaturePageLayout';
import { SubventionsMockup, AlertesMockup } from '../../components/features/ProductMockups';

const RELATED = [
  { label: 'Prévision de trésorerie', href: '/fonctionnalites/prevision-tresorerie' },
  { label: 'Stress test', href: '/fonctionnalites/stress-test-tresorerie' },
  { label: 'Alertes', href: '/fonctionnalites/alertes-tresorerie' },
  { label: 'Reporting financier', href: '/fonctionnalites/reporting-financier' },
];

interface Props { onNavigate?: (p: string) => void; onSignIn?: () => void; onSignUp?: () => void; }

export default function SuiviSubventionsPage({ onNavigate, onSignIn, onSignUp }: Props) {
  return (
    <FeaturePageLayout
      seo={{
        title: 'Suivi des subventions — Pilotez vos financements publics | TrezoOne',
        description: 'Suivez l\'état de chaque subvention, visualisez les retards et leur impact sur votre trésorerie. Conçu pour les associations et structures subventionnées.',
        canonical: '/fonctionnalites/suivi-subventions',
        keywords: 'suivi subventions associations, financement public, retard subvention, gestion subventions, impact trésorerie',
      }}
      activeNavId="subventions"
      hero={{
        tag: 'Suivi des subventions',
        title: 'Pilotez chaque subvention et anticipez les retards.',
        subtitle: 'Suivez l\'état de vos financements publics en temps réel. Visualisez ce qui est attendu, ce qui est reçu, ce qui est en retard — et l\'impact immédiat sur votre trésorerie.',
        mockup: <SubventionsMockup />,
      }}
      problem={{
        title: 'Vos subventions arrivent en retard et vous l\'apprenez toujours trop tard',
        points: [
          'Vous notez vos subventions dans un tableau Excel séparé, sans lien avec votre trésorerie réelle.',
          'Un retard de 2 mois sur une subvention principale peut tout déséquilibrer — mais vous ne le voyez pas à l\'avance.',
          'Vous ne savez pas rapidement quelles subventions sont en cours, en attente ou en retard.',
          'Vous perdez du temps à reconstituer l\'historique pour vos rapports intermédiaires.',
        ],
      }}
      steps={[
        { num: 1, title: 'Ajoutez vos subventions attendues', desc: 'Créez une fiche par financement : montant, bailleur, date attendue, conditions de versement.' },
        { num: 2, title: 'Suivez les versements réels', desc: 'Lors de l\'import de vos relevés, TrezoOne rapproche automatiquement les versements reçus avec les subventions attendues.' },
        { num: 3, title: 'Détectez les retards', desc: 'TrezoOne génère une alerte automatique dès qu\'une subvention dépasse sa date de versement prévue.' },
        { num: 4, title: 'Visualisez l\'impact', desc: 'Voir instantanément comment le retard affecte votre solde prévisionnel et votre capacité à payer vos charges.' },
      ]}
      screens={[<SubventionsMockup />, <AlertesMockup />]}
      useCases={[
        { audience: 'Associations', benefit: 'Suivez vos appels à projets, subventions annuelles et aides publiques en un seul endroit.' },
        { audience: 'Structures culturelles', benefit: 'Gérez les multiples financeurs (DRAC, Région, Ville, CAF) et leurs calendriers de versement.' },
        { audience: 'Trésorier(e)', benefit: 'Présentez un tableau de bord des subventions clair à votre CA ou à vos financeurs.' },
        { audience: 'Directeur(rice)', benefit: 'Anticipez les retards et déclenchez les relances au bon moment sans attendre la crise.' },
        { audience: 'RAF / DAF', benefit: 'Consolidez le suivi de plusieurs programmes de financement et leurs impacts sur le cash.' },
        { audience: 'Porteur de projet', benefit: 'Vérifiez l\'état de votre dossier et alignez vos dépenses sur les versements confirmés.' },
      ]}
      benefits={[
        'Ne plus jamais être surpris par un retard de subvention',
        'Anticiper les tensions de trésorerie liées aux décalages de financement',
        'Gagner du temps sur la préparation des rapports intermédiaires',
        'Rassurer vos partenaires avec une vision claire de vos financements',
        'Déclencher les relances bailleurs au bon moment',
        'Prendre des décisions de dépense sur la base de financements confirmés',
      ]}
      faq={[
        { q: 'Peut-on suivre des subventions pluriannuelles ?', a: 'Oui. TrezoOne permet de gérer des subventions sur plusieurs exercices, avec les tranches de versement prévues et reçues.' },
        { q: 'Comment TrezoOne détecte-t-il les retards ?', a: 'TrezoOne compare la date de versement prévue que vous avez saisie avec la date réelle d\'encaissement. Si aucun versement n\'est reçu à la date prévue, une alerte est générée.' },
        { q: 'Peut-on associer une subvention à un projet spécifique ?', a: 'Oui. Vous pouvez lier chaque subvention à un projet ou un programme pour suivre le taux de couverture par activité.' },
        { q: 'Le suivi des subventions est-il disponible sur tous les packs ?', a: 'Le suivi des subventions de base est disponible sur tous les packs. Les fonctionnalités avancées (rapprochement automatique, alertes) sont disponibles à partir du Pack Métier.' },
      ]}
      relatedLinks={RELATED}
      onNavigate={onNavigate}
      onSignIn={onSignIn}
      onSignUp={onSignUp}
    />
  );
}
